/**
 * useMapAllocations hook
 * Fetches aircraft allocations for 3D map rendering + business data for the floating card
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTenant } from '@/contexts'
import { supabase } from '@/lib/supabase'
import type { PlacedAircraft3D, AircraftPhysicalDimensions, ModelSettings } from '@/services/3d'
import type { AircraftCategory } from '@/types/aircraft-types'
import type {
  AllocationEnrichedData,
  AircraftClientInfo,
  AircraftDetailInfo,
  AllocationBusinessData,
  ContactInfo,
  FuelSummaryForLabels,
  LeaseContractInfo,
  TransientStayInfo,
} from '@/types/aircraft-card'
import type { AircraftStatus } from '@/types/aircrafts'
import type { PositionType, AllocationStatus, PresenceStatus } from '@/types/allocations'
import { deriveRenderMode } from '@/types/allocations'
import type { EscalationType, LeaseType, LeaseStatus, RateType } from '@/types/leases'
import type { BookingSource, TransientStatus, BookingType } from '@/types/transient-stays'
import type { ClientType, ClientStatus } from '@/types/clients'
import {
  calculateCommitmentProgress,
  isCommitmentAtRisk,
  getDaysRemainingInMonth,
} from '@/types/fuel-transactions'

// ============================================
// Raw query result types
// ============================================

interface RawContactData {
  id: string
  first_name: string
  last_name: string
  job_title: string | null
  email_primary: string | null
  phone_mobile: string | null
  is_primary_contact: boolean | null
  is_billing_contact: boolean | null
  is_operations_contact: boolean | null
  preferred_contact_method: string | null
  status: string
  deleted_at: string | null
}

interface RawClientData {
  id: string
  client_type: string
  first_name: string | null
  last_name: string | null
  company_name: string | null
  email_primary: string | null
  phone_primary: string | null
  status: string
  contacts: RawContactData[] | null
}

interface RawLeaseClientData {
  first_name: string | null
  last_name: string | null
  company_name: string | null
}

interface RawAircraftData {
  id: string
  tail_number: string
  aircraft_type_id: string
  manufacturer: string | null
  model: string | null
  length: number | null
  wingspan: number | null
  height: number | null
  tail_height: number | null
  empty_weight: number | null
  max_takeoff_weight: number | null
  registration_country: string | null
  serial_number: string | null
  year_manufactured: number | null
  annual_inspection_due: string | null
  insurance_expiry: string | null
  status: string
  notes: string | null
  owner_name: string | null
  default_contact_id: string | null
  default_contact: RawContactData | null
  aircraft_types: {
    id: string
    type_code: string
    manufacturer: string | null
    model: string | null
    model_url: string | null
    model_settings: ModelSettings | null
    category: AircraftCategory | null
    default_fuel_type: string | null
    length: number | null
    wingspan: number | null
    height: number | null
    tail_height: number | null
    footprint_sqft: number | null
  } | null
  owner: RawClientData | null
  operator: RawClientData | null
}

interface RawLeaseData {
  id: string
  contract_number: string
  type: string
  start_date: string
  end_date: string | null
  monthly_rate: number
  rate_basis: string | null
  security_deposit: number | null
  status: string
  auto_renew: boolean | null
  term_months: number | null
  fuel_commitment_gallons_monthly: number | null
  fuel_price_per_gallon: number | null
  fuel_price_type: string | null
  escalation_type: string | null
  escalation_pct: number | null
  termination_reason: string | null
  termination_date: string | null
  client_id: string | null
  location_id: string | null
  unit_id: string | null
  client: RawLeaseClientData | null
}

interface RawTransientData {
  id: string
  booking_number: string
  check_in: string
  expected_check_out: string
  actual_check_out: string | null
  rate_type: string | null
  rate_amount: number | null
  booking_type: string
  total_amount: number | null
  nights_count: number | null
  status: string
  notes: string | null
  source: string | null
  special_requests: string | null
  client: RawLeaseClientData | null
}

interface AllocationQueryResult {
  id: string
  aircraft_id: string
  position_x: number
  position_y: number
  position_z: number
  rotation: number
  status: string
  presence: string
  starts_at: string | null
  ends_at: string | null
  expected_return_at: string | null
  // Business data fields
  area_id: string | null
  unit_id: string | null
  position_type: string
  custom_label: string | null
  show_label: boolean | null
  color_override: string | null
  has_collision: boolean | null
  collision_details: unknown | null
  lease_contract_id: string | null
  transient_stay_id: string | null
  // Joined data
  aircraft: RawAircraftData | null
  area: { name: string } | null
  unit: { unit_code: string } | null
  lease_contract: RawLeaseData | null
  transient_stay: RawTransientData | null
}

// ============================================
// Fetch function
// ============================================

async function fetchAllocationsWithBusinessData(
  organizationId: string,
  locationId: string
): Promise<AllocationQueryResult[]> {
  const { data, error } = await supabase
    .from('allocations')
    .select(`
      id,
      aircraft_id,
      position_x,
      position_y,
      position_z,
      rotation,
      status,
      presence,
      starts_at,
      ends_at,
      expected_return_at,
      area_id,
      unit_id,
      position_type,
      custom_label,
      show_label,
      color_override,
      has_collision,
      collision_details,
      lease_contract_id,
      transient_stay_id,
      aircraft:aircraft_id (
        id,
        tail_number,
        aircraft_type_id,
        manufacturer,
        model,
        length,
        wingspan,
        height,
        tail_height,
        empty_weight,
        max_takeoff_weight,
        registration_country,
        serial_number,
        year_manufactured,
        annual_inspection_due,
        insurance_expiry,
        status,
        notes,
        owner_name,
        default_contact_id,
        default_contact:default_contact_id (
          id,
          first_name,
          last_name,
          job_title,
          email_primary,
          phone_mobile,
          is_primary_contact,
          is_billing_contact,
          is_operations_contact,
          preferred_contact_method,
          status,
          deleted_at
        ),
        aircraft_types:aircraft_type_id (
          id,
          type_code,
          model,
          model_url,
          model_settings,
          category,
          default_fuel_type,
          length,
          wingspan,
          height,
          tail_height
        ),
        owner:owner_client_id (
          id,
          client_type,
          first_name,
          last_name,
          company_name,
          email_primary,
          phone_primary,
          status,
          contacts (
            id,
            first_name,
            last_name,
            job_title,
            email_primary,
            phone_mobile,
            is_primary_contact,
            is_billing_contact,
            is_operations_contact,
            preferred_contact_method,
            status,
            deleted_at
          )
        ),
        operator:operator_client_id (
          id,
          client_type,
          first_name,
          last_name,
          company_name,
          email_primary,
          phone_primary,
          status,
          contacts (
            id,
            first_name,
            last_name,
            job_title,
            email_primary,
            phone_mobile,
            is_primary_contact,
            is_billing_contact,
            is_operations_contact,
            preferred_contact_method,
            status,
            deleted_at
          )
        )
      ),
      area:area_id (
        name
      ),
      unit:unit_id (
        unit_code
      ),
      lease_contract:lease_contract_id (
        id,
        contract_number,
        type,
        start_date,
        end_date,
        monthly_rate,
        rate_basis,
        security_deposit,
        status,
        auto_renew,
        term_months,
        fuel_commitment_gallons_monthly,
        fuel_price_per_gallon,
        fuel_price_type,
        escalation_type,
        escalation_pct,
        termination_reason,
        termination_date,
        client_id,
        location_id,
        unit_id,
        client:client_id (
          first_name,
          last_name,
          company_name
        )
      ),
      transient_stay:transient_stay_id (
        id,
        booking_number,
        check_in,
        expected_check_out,
        actual_check_out,
        rate_type,
        rate_amount,
        booking_type,
        total_amount,
        nights_count,
        status,
        notes,
        source,
        special_requests,
        client:client_id (
          first_name,
          last_name,
          company_name
        )
      )
    `)
    .eq('organization_id', organizationId)
    .eq('location_id', locationId)
    .in('status', ['active', 'reserved'])
    .is('deleted_at', null)
    // Guardrail against the default 1000-row cap. A single location is not expected to
    // exceed this many active/reserved allocations; paginate here if that ever changes.
    .limit(1000)

  if (error) throw error
  return (data || []) as unknown as AllocationQueryResult[]
}

// ============================================
// Batch fuel summary fetch
// ============================================

/**
 * Batch-fetch fuel transaction totals for the current month,
 * grouped by lease_contract_id, for all contracts with fuel commitments.
 * Returns a map from lease_contract_id to delivered gallons this month.
 */
async function fetchBatchFuelSummaries(
  contractIds: string[]
): Promise<Map<string, number>> {
  if (contractIds.length === 0) return new Map()

  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()

  const { data, error } = await supabase
    .from('fuel_transactions')
    .select('lease_contract_id, quantity_gallons')
    .in('lease_contract_id', contractIds)
    .eq('status', 'completed')
    .gte('fueled_at', monthStart)
    .is('deleted_at', null)

  if (error) {
    console.warn('Failed to fetch batch fuel summaries:', error.message)
    return new Map()
  }

  // Aggregate gallons by contract
  const map = new Map<string, number>()
  for (const row of data || []) {
    const contractId = row.lease_contract_id as string
    const current = map.get(contractId) || 0
    map.set(contractId, current + (row.quantity_gallons as number))
  }
  return map
}

/**
 * Batch-fetch fuel transaction totals for the current month,
 * grouped by aircraft_id, for all on-field aircraft.
 * Returns a map from aircraft_id to delivered gallons this month.
 */
async function fetchBatchAircraftFuelTotals(
  aircraftIds: string[]
): Promise<Map<string, number>> {
  if (aircraftIds.length === 0) return new Map()

  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()

  const { data, error } = await supabase
    .from('fuel_transactions')
    .select('aircraft_id, quantity_gallons')
    .in('aircraft_id', aircraftIds)
    .eq('status', 'completed')
    .gte('fueled_at', monthStart)
    .is('deleted_at', null)

  if (error) {
    console.warn('Failed to fetch batch aircraft fuel totals:', error.message)
    return new Map()
  }

  const map = new Map<string, number>()
  for (const row of data || []) {
    const aircraftId = row.aircraft_id as string
    const current = map.get(aircraftId) || 0
    map.set(aircraftId, current + (row.quantity_gallons as number))
  }
  return map
}

// ============================================
// Transform functions
// ============================================

/**
 * Transform raw query result to PlacedAircraft3D for 3D rendering (unchanged logic)
 */
function transformAllocationTo3D(allocation: AllocationQueryResult): PlacedAircraft3D | null {
  const aircraft = allocation.aircraft
  if (!aircraft) return null

  const aircraftType = aircraft.aircraft_types
  if (!aircraftType) return null

  const modelSettings: ModelSettings = aircraftType.model_settings || {}

  const modelUrl = aircraftType.model_url
    || modelSettings.models?.lod1?.url
    || modelSettings.filePath

  const effectiveModelSettings: ModelSettings = {
    ...modelSettings,
    filePath: modelUrl || undefined,
  }

  // Build physical dimensions for procedural rendering
  // Aircraft-level overrides take precedence over aircraft_type defaults
  const resolvedLength = aircraft.length ?? aircraftType.length
  const resolvedWingspan = aircraft.wingspan ?? aircraftType.wingspan
  const resolvedHeight = aircraft.height ?? aircraftType.height

  const physicalDimensions: AircraftPhysicalDimensions | undefined =
    resolvedLength && resolvedWingspan && resolvedHeight
      ? {
          length: resolvedLength,
          wingspan: resolvedWingspan,
          height: resolvedHeight,
          tailHeight: aircraft.tail_height ?? aircraftType.tail_height ?? null,
          category: aircraftType.category ?? null,
          gearType: null,
        }
      : undefined

  // Need either a model URL or physical dimensions for rendering
  if (!modelUrl && !physicalDimensions) return null

  return {
    id: allocation.id,
    aircraftId: allocation.aircraft_id,
    tailNumber: aircraft.tail_number,
    typeId: aircraftType.id,
    position: [
      Number(allocation.position_x) || 0,
      Number(allocation.position_y) || 0,
      Number(allocation.position_z) || 0,
    ],
    rotation: Number(allocation.rotation) || 0,
    scale: 1,
    aircraftType: {
      id: aircraftType.id,
      typeCode: aircraftType.type_code,
      modelSettings: effectiveModelSettings,
    },
    physicalDimensions,
    renderMode: deriveRenderMode(
      allocation.status as AllocationStatus,
      allocation.presence as PresenceStatus,
    ),
  }
}

/**
 * Transform raw client data to AircraftClientInfo
 */
function transformClient(raw: RawClientData | null): AircraftClientInfo | null {
  if (!raw) return null
  return {
    id: raw.id,
    clientType: raw.client_type as ClientType,
    firstName: raw.first_name,
    lastName: raw.last_name,
    companyName: raw.company_name,
    emailPrimary: raw.email_primary,
    phonePrimary: raw.phone_primary,
    status: raw.status as ClientStatus,
  }
}

/**
 * Transform raw contact data to ContactInfo[], filtering out soft-deleted
 */
function transformContacts(raw: RawContactData[] | null | undefined): ContactInfo[] | null {
  if (!raw || raw.length === 0) return null
  const active = raw.filter((c) => !c.deleted_at && c.status === 'active')
  if (active.length === 0) return null
  return active.map((c) => ({
    id: c.id,
    firstName: c.first_name,
    lastName: c.last_name,
    jobTitle: c.job_title,
    emailPrimary: c.email_primary,
    phoneMobile: c.phone_mobile,
    isPrimaryContact: c.is_primary_contact ?? false,
    isBillingContact: c.is_billing_contact ?? false,
    isOperationsContact: c.is_operations_contact ?? false,
    preferredContactMethod: c.preferred_contact_method,
  }))
}

/**
 * Build client display name from lease/transient client sub-join
 */
function buildClientName(raw: RawLeaseClientData | null): string | null {
  if (!raw) return null
  if (raw.company_name) return raw.company_name
  return [raw.first_name, raw.last_name].filter(Boolean).join(' ') || null
}

/**
 * Transform raw query result to AllocationEnrichedData for the floating card
 */
function transformToEnrichedData(
  allocation: AllocationQueryResult,
  fuelSummaryMap?: Map<string, number>,
  aircraftFuelMap?: Map<string, number>
): AllocationEnrichedData | null {
  const aircraft = allocation.aircraft
  if (!aircraft) return null

  const aircraftType = aircraft.aircraft_types
  const typeCode = aircraftType?.type_code || 'Unknown'

  // Transform default contact (single record, not array)
  const rawDc = aircraft.default_contact
  const defaultContact: ContactInfo | null =
    rawDc && !rawDc.deleted_at && rawDc.status === 'active'
      ? {
          id: rawDc.id,
          firstName: rawDc.first_name,
          lastName: rawDc.last_name,
          jobTitle: rawDc.job_title,
          emailPrimary: rawDc.email_primary,
          phoneMobile: rawDc.phone_mobile,
          isPrimaryContact: rawDc.is_primary_contact ?? false,
          isBillingContact: rawDc.is_billing_contact ?? false,
          isOperationsContact: rawDc.is_operations_contact ?? false,
          preferredContactMethod: rawDc.preferred_contact_method,
        }
      : null

  const aircraftDetail: AircraftDetailInfo = {
    id: aircraft.id,
    tailNumber: aircraft.tail_number,
    manufacturer: aircraft.manufacturer,
    model: aircraft.model || aircraftType?.model || null,
    typeCode,
    category: aircraftType?.category ?? null,
    length: aircraft.length,
    wingspan: aircraft.wingspan,
    height: aircraft.height,
    tailHeight: aircraft.tail_height,
    emptyWeight: aircraft.empty_weight,
    maxTakeoffWeight: aircraft.max_takeoff_weight,
    registrationCountry: aircraft.registration_country,
    serialNumber: aircraft.serial_number,
    yearManufactured: aircraft.year_manufactured,
    annualInspectionDue: aircraft.annual_inspection_due,
    insuranceExpiry: aircraft.insurance_expiry,
    status: aircraft.status as AircraftStatus,
    notes: aircraft.notes,
    ownerName: aircraft.owner_name,
    owner: transformClient(aircraft.owner),
    operator: transformClient(aircraft.operator),
    defaultContactId: aircraft.default_contact_id,
    defaultContact,
    defaultFuelType: (aircraftType?.default_fuel_type as 'jet_a' | 'avgas' | null) ?? null,
  }

  const status = allocation.status as AllocationStatus
  const presence = allocation.presence as PresenceStatus

  const allocationBusiness: AllocationBusinessData = {
    areaId: allocation.area_id,
    areaName: allocation.area?.name || null,
    unitId: allocation.unit_id,
    unitCode: allocation.unit?.unit_code || null,
    positionType: (allocation.position_type || 'unknown') as PositionType,
    customLabel: allocation.custom_label,
    showLabel: allocation.show_label,
    colorOverride: allocation.color_override,
    hasCollision: allocation.has_collision,
    collisionDetails: allocation.collision_details,
    leaseContractId: allocation.lease_contract_id,
    transientStayId: allocation.transient_stay_id,
    status,
    presence,
    renderMode: deriveRenderMode(status, presence),
    startsAt: allocation.starts_at,
    endsAt: allocation.ends_at,
    expectedReturnAt: allocation.expected_return_at,
  }

  let leaseContract: LeaseContractInfo | null = null
  if (allocation.lease_contract) {
    const lc = allocation.lease_contract
    leaseContract = {
      id: lc.id,
      contractNumber: lc.contract_number,
      type: lc.type as LeaseType,
      startDate: lc.start_date,
      endDate: lc.end_date,
      monthlyRate: lc.monthly_rate,
      rateBasis: (lc.rate_basis || null) as RateType | null,
      securityDeposit: lc.security_deposit,
      status: lc.status as LeaseStatus,
      autoRenew: lc.auto_renew,
      termMonths: lc.term_months,
      clientName: buildClientName(lc.client),
      clientId: lc.client_id,
      locationId: lc.location_id,
      unitId: lc.unit_id,
      fuelCommitmentGallonsMonthly: lc.fuel_commitment_gallons_monthly,
      fuelPricePerGallon: lc.fuel_price_per_gallon,
      fuelPriceType: lc.fuel_price_type,
      escalationType: (lc.escalation_type || null) as EscalationType | null,
      escalationPct: lc.escalation_pct,
      terminationReason: lc.termination_reason,
      terminationDate: lc.termination_date,
    }
  }

  let transientStay: TransientStayInfo | null = null
  if (allocation.transient_stay) {
    const ts = allocation.transient_stay
    transientStay = {
      id: ts.id,
      bookingNumber: ts.booking_number,
      checkIn: ts.check_in,
      expectedCheckOut: ts.expected_check_out,
      actualCheckOut: ts.actual_check_out,
      rateType: (ts.rate_type || null) as RateType | null,
      rateAmount: ts.rate_amount,
      bookingType: (ts.booking_type || 'guaranteed') as BookingType,
      totalAmount: ts.total_amount,
      nightsCount: ts.nights_count,
      status: ts.status as TransientStatus,
      notes: ts.notes,
      clientName: buildClientName(ts.client),
      source: (ts.source || null) as BookingSource | null,
      specialRequests: ts.special_requests,
    }
  }

  // Build fuel summary — commitment-based (lease) or activity-only (all aircraft)
  let fuelSummary: FuelSummaryForLabels | null = null
  if (leaseContract?.fuelCommitmentGallonsMonthly && leaseContract.fuelCommitmentGallonsMonthly > 0 && fuelSummaryMap) {
    // Case 1: Lease with fuel commitment — show progress against target
    const commitmentGallons = leaseContract.fuelCommitmentGallonsMonthly
    const deliveredGallons = fuelSummaryMap.get(leaseContract.id) || 0
    const percentComplete = calculateCommitmentProgress(deliveredGallons, commitmentGallons)
    const daysRemaining = getDaysRemainingInMonth()
    fuelSummary = {
      commitmentGallonsMonthly: commitmentGallons,
      deliveredGallonsMonthly: deliveredGallons,
      percentComplete,
      isAtRisk: isCommitmentAtRisk(percentComplete, daysRemaining),
    }
  } else if (aircraftFuelMap) {
    // Case 2: No commitment — show activity only if aircraft has fueling this month
    const deliveredGallons = aircraftFuelMap.get(allocation.aircraft_id) || 0
    if (deliveredGallons > 0) {
      fuelSummary = {
        commitmentGallonsMonthly: null,
        deliveredGallonsMonthly: deliveredGallons,
        percentComplete: null,
        isAtRisk: false,
      }
    }
  }

  return {
    allocationId: allocation.id,
    aircraft: aircraftDetail,
    allocation: allocationBusiness,
    leaseContract,
    transientStay,
    ownerContacts: transformContacts(aircraft.owner?.contacts),
    operatorContacts: transformContacts(aircraft.operator?.contacts),
    fuelSummary,
  }
}

// ============================================
// Hook
// ============================================

interface UseMapAllocationsResult {
  /** Aircraft transformed for 3D rendering */
  aircraft: PlacedAircraft3D[]
  /** Enriched business data for the floating card, keyed by allocation ID */
  enrichedData: Map<string, AllocationEnrichedData>
  /** Loading state */
  isLoading: boolean
  /** Error state */
  error: Error | null
}

/**
 * Hook to fetch aircraft allocations for 3D map rendering and business card data
 * @param locationId - Location ID to fetch allocations for
 * @returns Aircraft for 3D rendering + enriched business data map
 */
export function useMapAllocations(locationId: string | undefined): UseMapAllocationsResult {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  const {
    data: queryResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['map-allocations', organizationId, locationId],
    queryFn: async () => {
      const allocations = await fetchAllocationsWithBusinessData(organizationId!, locationId!)

      // Collect contract IDs that have fuel commitments
      const contractIdsWithFuel = allocations
        .filter((a) => a.lease_contract?.fuel_commitment_gallons_monthly && a.lease_contract.fuel_commitment_gallons_monthly > 0)
        .map((a) => a.lease_contract!.id)

      // Collect all aircraft IDs for activity-only fuel query
      const allAircraftIds = allocations
        .map((a) => a.aircraft_id)
        .filter(Boolean)

      // Batch-fetch fuel transaction totals for the current month
      const [fuelMap, aircraftFuelMap] = await Promise.all([
        fetchBatchFuelSummaries([...new Set(contractIdsWithFuel)]),
        fetchBatchAircraftFuelTotals([...new Set(allAircraftIds)]),
      ])

      return { allocations, fuelMap, aircraftFuelMap }
    },
    enabled: !!organizationId && !!locationId,
    // Allocations (field state) change infrequently relative to schedules; keep them fresh
    // for 5 min so day-to-day toggling on the board doesn't refetch them.
    staleTime: 5 * 60_000,
  })

  const allocations = queryResult?.allocations
  const fuelMap = queryResult?.fuelMap
  const aircraftFuelMap = queryResult?.aircraftFuelMap

  // Transform allocations to 3D format (unchanged)
  const aircraft = useMemo(() => {
    if (!allocations) return []
    return allocations
      .map(transformAllocationTo3D)
      .filter((a): a is PlacedAircraft3D => a !== null)
  }, [allocations])

  // Transform allocations to enriched business data map
  const enrichedData = useMemo(() => {
    const map = new Map<string, AllocationEnrichedData>()
    if (!allocations) return map
    for (const alloc of allocations) {
      const enriched = transformToEnrichedData(alloc, fuelMap, aircraftFuelMap)
      if (enriched) {
        map.set(alloc.id, enriched)
      }
    }
    return map
  }, [allocations, fuelMap, aircraftFuelMap])

  return {
    aircraft,
    enrichedData,
    isLoading,
    error: error as Error | null,
  }
}
